import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAssetMeta } from '../lib/assets';
import '../components/Assets.css';
import BackLink from '../components/asset/BackLink';
import { cameFrom } from '../components/asset/BackLink';

/**
 * Approve plugged-in devices (Gordon, 30 October): what a laptop reported
 * plugged into it, waiting for IT before it joins the register - one card
 * per laptop. Each thing goes to the laptop's holder (plugged into the
 * laptop), onto a desk (one already there, or a new one, hot or somebody's)
 * as the desk's kit, is the person's own, or is ignored; its category can be
 * changed on the way. Nothing waits here while "Link plugged-in devices at
 * once" is on, so the page offers to switch it off.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (t) => (t ? new Date(t).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/** A desk-default kind goes to a desk unless said otherwise; the rest to the person. */
const DESK_KINDS = ['monitor', 'dock', 'display_device'];

export default function AssetDiscoveryApprove() {
  const meta = useAssetMeta();
  const [data, setData] = useState(null);
  const [desks, setDesks] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    axios.get(`${API_BASE}/assets/discoveries/approvals`)
      .then((r) => { setData(r.data); setLoadError(''); })
      .catch((e) => setLoadError(errorOf(e, 'Could not load what is waiting.')));
    axios.get(`${API_BASE}/assets/desks`).then((r) => setDesks(r.data?.desks || [])).catch(() => setDesks([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const linkAtOnce = async (value) => {
    try {
      await axios.put(`${API_BASE}/app-settings/assets-attached-autolink`, { value });
      load();
    } catch (e) { setLoadError(errorOf(e, 'Could not change that setting.')); }
  };

  if (loadError && !data) {
    return (
      <div className="container">
        <p><BackLink fallback={{ label: 'Discovery', path: '/assets/discovery' }} /></p>
        <p className="as-note" data-tone="warning" role="alert">{loadError}</p>
      </div>
    );
  }
  const groups = data?.groups || [];

  return (
    <div className="container">
      <p><BackLink fallback={{ label: 'Discovery', path: '/assets/discovery' }} /></p>
      <div className="page-header">
        <h1 className="page-title">Approve plugged-in devices</h1>
        <p className="page-description">
          What each laptop reported plugged into it, waiting before it joins the register. Give each thing to the
          laptop’s holder, put it on a desk (one there already, or a new hot or personal desk), mark it personal,
          or ignore it - and change its category if it is wrong.
        </p>
      </div>

      {data && data.autolink && (
        <p className="as-note" data-tone="info" data-testid="approve-autolink-on">
          Plugged-in devices are linked to the laptop’s holder at once, so they do not wait here.{' '}
          <button type="button" className="btn btn-secondary btn-small" onClick={() => linkAtOnce(false)}>
            Approve them here first
          </button>
        </p>
      )}
      {data && !data.autolink && (
        <p className="as-quiet">
          New plugged-in devices wait here for approval.{' '}
          <button type="button" className="btn btn-plain btn-small" onClick={() => linkAtOnce(true)}>Link them at once instead</button>
        </p>
      )}
      {loadError && <p className="as-note" data-tone="warning" role="alert">{loadError}</p>}
      {note && <p className="as-note" data-tone="success" role="status">{note}</p>}

      {!data ? <p className="as-quiet">Loading…</p>
        : !groups.length ? <div className="empty-state"><p>Nothing is waiting for approval.</p></div>
          : groups.map((g) => (
            <LaptopCard key={g.laptop.id} g={g} meta={meta} desks={desks}
              onDone={(text) => { setNote(text); load(); }} />
          ))}
    </div>
  );
}

function LaptopCard({ g, meta, desks, onDone }) {
  const categories = (meta?.categories || []).filter((c) => c.active !== false);
  const catId = (name) => categories.find((c) => c.name.toLowerCase() === String(name || '').toLowerCase())?.id || '';
  const holder = g.laptop.holder_label;
  const [rows, setRows] = useState(() => g.items.map((i) => ({
    id: i.id,
    to: DESK_KINDS.includes(i.kind) && i.seen_at_desk ? 'desk' : holder ? 'holder' : 'desk',
    category_id: ''
  })));
  // The desk, for the things sent to one: the desk the laptop sits at, else one picked or made.
  const [desk, setDesk] = useState(() => g.items.find((i) => i.seen_at_desk)?.seen_at_desk?.id || '');
  const [fresh, setFresh] = useState({ name: '', parent_id: g.laptop.location_id || '', desk_mode: 'hot', user_id: g.laptop.holder_kind === 'user' ? g.laptop.holder_id : '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const all = (to) => setRows((rs) => rs.map((r) => ({ ...r, to })));
  const toDesk = rows.some((r) => r.to === 'desk');
  const places = (meta?.locations || []).filter((l) => l.active !== false
    && !['desk', 'storage', 'container', 'shelf'].includes(l.kind));

  const approve = async () => {
    setBusy(true);
    setError('');
    // The category shown is the one sent: the suggestion unless it was changed.
    const shown = (r) => r.category_id || catId(g.items.find((i) => i.id === r.id)?.suggested_category) || undefined;
    const body = { items: rows.filter((r) => r.to !== 'skip').map((r) => ({ id: r.id, to: r.to, category_id: shown(r) })) };
    if (toDesk) {
      if (desk === 'new') body.new_desk = { ...fresh, user_id: fresh.desk_mode === 'fixed' ? fresh.user_id : undefined };
      else body.desk_id = desk || undefined;
    }
    try {
      const r = await axios.post(`${API_BASE}/assets/discoveries/approvals`, body);
      const n = r.data?.done?.length || 0;
      onDone(`${g.laptop.asset_tag}: ${n} approved${r.data?.desk ? `, desk ${r.data.desk.name}` : ''}.`);
    } catch (e) {
      setError(errorOf(e, 'Could not approve those.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="as-panel as-approve" data-testid="approve-card" data-laptop={g.laptop.asset_tag} style={{ marginBottom: '1rem' }}>
      <div className="as-diag-head">
        <h2>
          <Link to={`/assets/${g.laptop.id}`} state={cameFrom('Approve devices')}>{g.laptop.asset_tag}</Link>
          <span className="as-quiet">{` · ${holder || 'nobody holds it'}${g.laptop.place ? ` · ${g.laptop.place}` : ''}`}</span>
        </h2>
        <span className="as-spacer" />
        {holder && <button type="button" className="btn btn-plain btn-small" onClick={() => all('holder')}>{`All to ${holder}`}</button>}
        <button type="button" className="btn btn-plain btn-small" onClick={() => all('desk')}>All to a desk</button>
      </div>

      <table className="as-table">
        <thead>
          <tr><th>Device</th><th>Seen</th><th>Category</th><th>Goes to</th></tr>
        </thead>
        <tbody>
          {g.items.map((i) => {
            const row = rows.find((r) => r.id === i.id);
            return (
              <tr key={i.id} data-id={i.id}>
                <td>
                  <strong>{[i.manufacturer, i.model].filter(Boolean).join(' ') || i.kind_label}</strong>
                  <span className="as-sub">{[i.kind_label, i.serial_number && `S/N ${i.serial_number}`,
                    i.seen_at_desk && `at ${i.seen_at_desk.name}`].filter(Boolean).join(' · ')}</span>
                </td>
                <td className="as-quiet">{when(i.first_seen_at)}{i.seen_where && i.seen_where !== 'office' ? ` · ${i.seen_where === 'away' ? 'at home' : i.seen_where}` : ''}</td>
                <td>
                  <select aria-label={`Category of ${i.model || i.kind_label}`} value={row.category_id || catId(i.suggested_category)}
                    onChange={(e) => set(i.id, { category_id: e.target.value })} disabled={row.to === 'ignore' || row.to === 'skip'}>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </td>
                <td>
                  <select aria-label={`Where ${i.model || i.kind_label} goes`} value={row.to} onChange={(e) => set(i.id, { to: e.target.value })}>
                    {holder && <option value="holder">{`${holder} (with the laptop)`}</option>}
                    <option value="desk">A desk</option>
                    <option value="personal">Personal - not the firm’s</option>
                    <option value="ignore">Ignore</option>
                    <option value="skip">Decide later</option>
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {toDesk && (
        <div className="as-inline-form" data-testid="approve-desk" style={{ marginTop: '0.75rem' }}>
          <div className="form-group">
            <label htmlFor={`desk-${g.laptop.id}`}>Desk</label>
            <select id={`desk-${g.laptop.id}`} value={desk} onChange={(e) => setDesk(e.target.value)}>
              <option value="">Choose a desk…</option>
              <option value="new">+ A new desk</option>
              {desks.map((d) => <option key={d.id} value={d.id}>{d.label ? `${d.path} (${d.label})` : d.path}</option>)}
            </select>
          </div>
          {desk === 'new' && (
            <>
              <div className="form-group">
                <label htmlFor={`dn-${g.laptop.id}`}>Name</label>
                <input id={`dn-${g.laptop.id}`} value={fresh.name} placeholder="Desk 2.14" maxLength={120}
                  onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label htmlFor={`dp-${g.laptop.id}`}>In</label>
                <select id={`dp-${g.laptop.id}`} value={fresh.parent_id} onChange={(e) => setFresh({ ...fresh, parent_id: e.target.value })}>
                  <option value="">Choose the room or floor…</option>
                  {places.map((p) => <option key={p.id} value={p.id}>{p.path}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={`dw-${g.laptop.id}`}>Whose</label>
                <select id={`dw-${g.laptop.id}`} value={fresh.desk_mode === 'hot' ? 'hot' : fresh.user_id}
                  onChange={(e) => setFresh(e.target.value === 'hot' ? { ...fresh, desk_mode: 'hot' }
                    : { ...fresh, desk_mode: 'fixed', user_id: e.target.value })}>
                  <option value="hot">Hot desk - whoever sits there</option>
                  {(meta?.users || []).map((u) => <option key={u.id} value={u.id}>{`${u.display_name || u.email}’s desk`}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      )}

      {error && <p className="as-note" data-tone="warning" role="alert">{error}</p>}
      <p style={{ marginTop: '0.75rem' }}>
        <button type="button" className="btn btn-primary" disabled={busy || rows.every((r) => r.to === 'skip')} onClick={approve}>
          Approve
        </button>
      </p>
    </section>
  );
}
